import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useIntl } from 'react-intl';
import { useMediaQuery, useTheme } from '@mui/material';
import {
  notifySMSReceivedAtom,
  notifyMailReceivedAtom,
  visibilityStateAtom,
} from '../Atoms/HubAtoms';
import Navbar from './Navbar/Navbar';
import Sidebar from './Sidebar';
import Main from '../Router/MainRouter';
import NotificationDrawer from './Navbar/NotificationDrawer';
import useInfiniteQuery from '../Hooks/useInfiniteQuery';
import '../Styles/app.scss';
import {
  occupationsAtom,
  productsAtom,
  unreadMessagesCounterAtom,
  maxDistanceAtom,
  selectedCustomerIdAtom,
  selectedLeadIdAtom,
  additionalCustomerDataAtom,
  practiceProductAtom,
  alertDialogAtom,
  allOutcomesAtom,
  outcomesAtom,
  activityTypesAtom,
  activityOutcomesAtom,
} from '../Atoms';
import { notifyCacheRefreshAtom } from '../Atoms/HubAtoms';
import sortByProperty from '../Helpers/Arrays/sortByProperty';
import useAPI from '../api';
import parseValuesToBoolean from '../Helpers/parseValuesToBoolean';
import keysToLowerCase from '../Helpers/keysToLowerCase';
import useLocationName from '../Hooks/useLocationName';
import { FullPageFlexBox, FullSizeColumn } from './FlexBox';
import { APP_ROUTES } from '../Router/routes';

const openDrawerWidth = 300;
const closedDrawerWidth = 72;
const DBEMainApp = function DBEMainApp({
  loggedUserId, //id
  loggedUserFullName, //name and surname
  onLogout,
  onChangePassword,
  userRoles = null,
  selectedBranchId,
}) {
  const API = useAPI();
  const intl = useIntl();
  const theme = useTheme();
  const navigate = useNavigate();
  const locationName = useLocationName();

  /**Media queries**/
  const desktop = useMediaQuery('(min-width: 1300px)');
  const tablet = useMediaQuery((Theme) => Theme.breakpoints.up('md'));
  const mobile = useMediaQuery((Theme) => Theme.breakpoints.up('sm'));
  const heightQuery = useMediaQuery('(min-height:750px)');
  /****/

  /**Atoms**/
  const setSectionOutcomes = useSetAtom(outcomesAtom);
  const setAllOutcomes = useSetAtom(allOutcomesAtom);
  const setAlertDialog = useSetAtom(alertDialogAtom);
  const notifyCacheRefresh = useAtomValue(notifyCacheRefreshAtom);

  const [selectedCustomerId, setSelectedCustomerId] = useAtom(
    selectedCustomerIdAtom
  );
  const setSelectedLeadId = useSetAtom(selectedLeadIdAtom);
  const setAdditionalCustomerData = useSetAtom(additionalCustomerDataAtom);
  const notifyMailReceived = useAtomValue(notifyMailReceivedAtom);
  const notifySMSReceived = useAtomValue(notifySMSReceivedAtom);
  const setVisiblityState = useSetAtom(visibilityStateAtom);
  const setMaxDistance = useSetAtom(maxDistanceAtom);
  const additionalCustomerData = useAtomValue(additionalCustomerDataAtom);
  const [activityOutcomes, setActivityOutcomes] = useAtom(activityOutcomesAtom);
  const [occupations, setOccupations] = useAtom(occupationsAtom);
  const [products, setProducts] = useAtom(productsAtom);
  const [unreadMessagesCounter, setUnreadMessagesCounter] = useAtom(
    unreadMessagesCounterAtom
  );
  const [practiceProducts, setPracticeProducts] = useAtom(practiceProductAtom);
  const [activityTypes, setActivityTypes] = useAtom(activityTypesAtom);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [changePwdError, setChangePwdError] = useState(null);
  const [remainingSMS, setRemainingSMS] = useState(null);
  const [version, setVersion] = useState(null);
  const [callTargets, setCallTargets] = useState(null);
  const [priorities, setPriorities] = useState([]);
  const [userRedemptionData, setUserRedemptionData] = useState(null);

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      setVisiblityState(true);
    } else {
      setVisiblityState(false);
    }
  });

  /**Media queries match**/
  useEffect(() => {
    if (!desktop && mobile && tablet) {
      setSidebarOpen(false);
    } else {
      setSidebarOpen(true);
    }
  }, [desktop]);

  const handleChangeSection = (page, extraParams) => {
    setSelectedCustomerId('');
    setSelectedLeadId('');
    navigate(page, { state: extraParams });
  };

  useEffect(() => {
    if (activityOutcomes && locationName) {
      sessionStorage.setItem('currentSection', locationName);
      const sectionOutcomes =
        activityOutcomes.CALL?.filter(({ extensionData: { sections } }) =>
          sections?.find((x) =>
            additionalCustomerData?.outcomesFilter
              ? x === additionalCustomerData.outcomesFilter
              : x === locationName
          )
        ) || [];
      setSectionOutcomes(sectionOutcomes);
    }
  }, [locationName, activityOutcomes, additionalCustomerData?.outcomesFilter]);

  /**Notifications**/
  useEffect(() => {
    //increment when 'notifySMSReceived' message is received
    if (notifySMSReceived || notifyMailReceived)
      setUnreadMessagesCounter(unreadMessagesCounter + 1);
  }, [notifySMSReceived?.id, notifyMailReceived?.id]);

  const getMessages = useCallback(
    (pageNumber) => {
      return selectedBranchId
        ? API.getEntityMessages(selectedBranchId, true, {
            pageNumber,
            silent: true,
          })
        : Promise.resolve([]);
    },
    [selectedBranchId]
  );
  const { recordCount, setNewItems, removeClickedItem, ObserverWrapper } =
    useInfiniteQuery(getMessages);
  /****/

  /**Max distance KM**/
  useEffect(() => {
    if (selectedBranchId) {
      API.getBranchMaxDistance(
        selectedBranchId,
        (res) => {
          if (res?.status === 204) setMaxDistance(0);
          else setMaxDistance(res);
        },
        () => {
          setMaxDistance(0);
        }
      );
    }
  }, [selectedBranchId]);
  /****/

  /**Branch data**/
  useEffect(() => {
    if (selectedBranchId) {
      API.getUnreadMessagesCount(
        selectedBranchId,
        (result) => {
          setUnreadMessagesCounter(result);
        },
        (err) => {
          console.log(err);
        }
      );
    }
  }, [selectedBranchId]);

  useEffect(() => {
    if (!selectedBranchId || selectedCustomerId) return;

    API.getCallTargets(
      selectedBranchId,
      (res) => {
        setCallTargets(res);
      },
      () => {}
    );
  }, [selectedBranchId, selectedCustomerId, locationName]);
  /****/

  /**Occupations**/
  useEffect(() => {
    if (
      (!occupations ||
        !Array.isArray(occupations) ||
        occupations.length === 0) &&
      loggedUserId
    ) {
      API.getOccupations(
        (result) => {
          setOccupations(result);
        },
        () => {
          setOccupations(null);
        }
      );
    }
  }, [loggedUserId]);
  /****/

  useEffect(() => {
    if (!loggedUserId) return;

    API.getUserRollingRedemption(
      (res) => {
        setUserRedemptionData(res);
      },
      () => {
        setUserRedemptionData(null);
      }
    );
  }, [loggedUserId]);

  /**Remaining SMS**/
  useEffect(() => {
    if (loggedUserId) {
      API.getCreditsSettings(
        ({ credits, alertThreshold, warningThreshold }) => {
          const smsNumber = !isNaN(credits) ? parseInt(credits, 10) : null;
          setRemainingSMS(smsNumber);
          if (credits < alertThreshold) {
            setAlertDialog({
              severity: 'error',
              title: intl.messages.ALERT,
              message: `${intl.messages.ALERTTHRESHOLD_MESSAGE}${credits}`,
            });
          } else if (credits < warningThreshold) {
            setAlertDialog({
              severity: 'warning',
              title: intl.messages.WARNING,
              message: `${intl.messages.WARNINGTHRESHOLD_MESSAGE}${credits}`,
            });
          }
        },
        () => {
          setRemainingSMS(null);
        }
      );
    }
  }, [loggedUserId]);
  /****/

  /**Products**/
  useEffect(() => {
    if (!selectedBranchId || !locationName) return;

    API.getAllProducts(
      selectedBranchId,
      locationName === 'MASSIVE_SENDING' ? 'CUSTOMERS' : locationName,
      (result) => {
        const sortedProducts = sortByProperty(
          result?.map(({ productName }) => productName),
          null,
          'ASCENDING'
        );
        setProducts(sortedProducts);
      },
      () => {
        setProducts(null);
      }
    );
  }, [selectedBranchId, locationName]);
  /****/

  /**Practice products**/
  useEffect(() => {
    if (
      (!practiceProducts ||
        !Array.isArray(practiceProducts) ||
        practiceProducts.length === 0) &&
      loggedUserId
    ) {
      API.getPracticeProducts(
        (result) => {
          setPracticeProducts(result?.sort((a, b) => b - a));
        },
        () => {
          setPracticeProducts(null);
        }
      );
    }
  }, [loggedUserId]);
  /****/

  const loadActivityOutcomes = (activityTypesArray) => {
    return API.getActivitiesOutcomes(
      (result) => {
        let groupedOutcomes = {};

        activityTypesArray.forEach((type) => {
          const typeOutcomes = result.filter(
            ({ activityTypeId, isActive }) =>
              isActive && activityTypeId === type?.id
          );
          const colors = theme.custom.generateConsistentUniqueColors(
            typeOutcomes.map(({ name }) => name)
          );
          groupedOutcomes = {
            ...groupedOutcomes,
            [[type?.name]]: typeOutcomes.map(
              ({ extensionData, ...data }, index) => ({
                ...data,
                color: colors[index],
                extensionData: keysToLowerCase(
                  parseValuesToBoolean(JSON.parse(extensionData))
                ),
              })
            ),
          };
        });

        setAllOutcomes(groupedOutcomes.CALL || []);
        setActivityOutcomes(groupedOutcomes);
      },
      () => {
        setActivityOutcomes([]);
      }
    );
  };

  useEffect(() => {
    API.getActivityTypes(
      (result) => {
        setActivityTypes([{ id: -1, name: 'MISSEDCALL' }, ...result]);
        loadActivityOutcomes(result);
      },
      () => {
        setActivityTypes([]);
      }
    );
  }, []);

  const changeSubmit = (data) => {
    if (data.newPassword === data.confirmPassword) {
      API.changePassword(
        {
          oldPassword: data.oldPassword,
          newPassword: data.newPassword,
        },
        () => onChangePassword(),
        (err) => {
          setChangePwdError(err);
        }
      );
    } else {
      setChangePwdError(intl.messages.PASSWORDS_MISMATCH);
    }
  };

  useEffect(() => {
    API.getCurrentVersion(
      (res) => {
        setVersion(res);
      },
      () => {}
    );
  }, []);

  const reloadActivityOutcomes = useCallback(() => {
    loadActivityOutcomes(activityTypes);
  }, [activityTypes]);

  useEffect(() => {
    if (!selectedBranchId) return;
    API.getActivityPriority(
      selectedBranchId,
      { silent: locationName !== 'PRIORITIES' },
      (res) => {
        setPriorities(res);
      },
      () => {
        setPriorities([]);
      }
    );
  }, [selectedBranchId, notifyCacheRefresh]);

  return (
    <div
      style={{
        display: loggedUserId && mobile ? 'block' : 'none',
      }}
    >
      <FullPageFlexBox>
        <Sidebar
          version={version}
          drawerWidth={sidebarOpen ? openDrawerWidth : closedDrawerWidth}
          desktop={desktop}
          handleChangeSection={handleChangeSection}
          open={sidebarOpen}
          toggleSidebar={() => setSidebarOpen(!sidebarOpen)}
          userFullName={loggedUserFullName}
          userRoles={userRoles}
        />

        <FullSizeColumn sx={{ overflow: 'hidden', minWidth: 0 }}>
          <Navbar
            // eslint-disable-next-line no-undef
            title={APP_NAME}
            onLogout={onLogout}
            unreadMessagesCounter={unreadMessagesCounter}
            desktop={desktop}
            sidebarOpen={sidebarOpen}
            handleChangeSection={handleChangeSection}
            openSidebar={() => setSidebarOpen((prev) => !prev)}
            remainingSMS={remainingSMS}
            userRoles={userRoles}
            callTargets={callTargets}
            priorities={priorities}
            userRedemptionData={userRedemptionData}
          />
          <Main
            sidebarOpen={sidebarOpen}
            userRoles={userRoles}
            changePwdError={changePwdError}
            changeSubmit={changeSubmit}
            desktop={desktop}
            open={sidebarOpen}
            tablet={tablet}
            mobile={mobile}
            heightQuery={heightQuery}
            handleChangeSection={handleChangeSection}
            setNewItems={setNewItems}
            removeClickedItem={removeClickedItem}
            setUnreadMessagesCounter={setUnreadMessagesCounter}
            unreadMessagesCounter={unreadMessagesCounter}
            recordCount={recordCount}
            reloadActivityOutcomes={reloadActivityOutcomes}
            priorities={priorities}
          />
        </FullSizeColumn>
      </FullPageFlexBox>
      <NotificationDrawer
        ObserverWrapper={ObserverWrapper}
        setNewItems={setNewItems}
        removeClickedItem={removeClickedItem}
        recordCount={recordCount}
        onClick={(customerId, name) => {
          setSelectedCustomerId(customerId);
          setAdditionalCustomerData({
            customerName: name,
            wavePeriod: null,
          });
          navigate(APP_ROUTES.CUSTOMERS);
        }}
        setUnreadMessagesCounter={setUnreadMessagesCounter}
        unreadMessagesCounter={unreadMessagesCounter}
      />
    </div>
  );
};

DBEMainApp.propTypes = {
  loggedUserId: PropTypes.string.isRequired,
  loggedUserFullName: PropTypes.string.isRequired,
  onLogout: PropTypes.func.isRequired,
  onChangePassword: PropTypes.func.isRequired,
  userRoles: PropTypes.array,
  selectedBranchId: PropTypes.string.isRequired,
  currentVersion: PropTypes.string.isRequired,
};

export default DBEMainApp;
